import { useRef, useEffect } from 'react'

type GradientDotsProps = {
  /** Dot size (default: 8) */
  dotSize?: number
  /** Spacing between dots (default: 10) */
  spacing?: number
  /** Animation duration in seconds (default: 30) */
  duration?: number
  /** Color cycle duration in seconds (default: 6) */
  colorCycleDuration?: number
  /** Background color (default: '#0C1430') */
  backgroundColor?: string
  className?: string
}

export function GradientDots({
  dotSize = 8,
  spacing = 10,
  duration = 30,
  colorCycleDuration = 6,
  backgroundColor = '#0C1430',
  className = '',
}: GradientDotsProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const hexSpacing = spacing * 1.732

    /* Inject scoped keyframes once */
    const styleId = 'gradient-dots-keyframes'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = `
        @keyframes gd-move {
          0% {
            background-position:
              0px 0px,
              ${spacing / 2}px ${hexSpacing / 2}px,
              0% 0%,
              0% 0%,
              0% 0%,
              0% 0px;
          }
          100% {
            background-position:
              0px 0px,
              ${spacing / 2}px ${hexSpacing / 2}px,
              800% 400%,
              1000% -400%,
              -1200% -600%,
              400% ${hexSpacing}px;
          }
        }
        @keyframes gd-hue {
          0%   { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
      `
      document.head.appendChild(style)
    }

    el.style.animation = `gd-move ${duration}s linear infinite, gd-hue ${colorCycleDuration}s linear infinite`
  }, [spacing, duration, colorCycleDuration])

  const hexSpacing = spacing * 1.732

  return (
    <div
      ref={ref}
      className={`absolute inset-0 ${className}`}
      style={{
        backgroundColor,
        backgroundImage: `
          radial-gradient(circle at 50% 50%, transparent 1.5px, ${backgroundColor} 0 ${dotSize}px, transparent ${dotSize}px),
          radial-gradient(circle at 50% 50%, transparent 1.5px, ${backgroundColor} 0 ${dotSize}px, transparent ${dotSize}px),
          radial-gradient(circle at 50% 50%, #f00, transparent 60%),
          radial-gradient(circle at 50% 50%, #ff0, transparent 60%),
          radial-gradient(circle at 50% 50%, #0f0, transparent 60%),
          radial-gradient(ellipse at 50% 50%, #00f, transparent 60%)
        `,
        backgroundSize: `
          ${spacing}px ${hexSpacing}px,
          ${spacing}px ${hexSpacing}px,
          200% 200%,
          200% 200%,
          200% 200%,
          200% ${hexSpacing}px
        `,
        backgroundPosition: `
          0px 0px, ${spacing / 2}px ${hexSpacing / 2}px,
          0% 0%,
          0% 0%,
          0% 0px
        `,
      }}
    />
  )
}
